import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import cx from 'classnames';
import {createPortal} from 'react-dom';
import {
  ACCOUNT_TEXT_MAX_LENGTH,
  isValidAccountText,
  notifyInvalidAccountText,
} from '../AccountSelect/accountTextValidation';
import {QEPInput} from '../QEPInput/qep-input';
import './OrderDetailsModal.scss';

export type OrderDetailsDraft = {
  memo?: string;
  customTag1?: string;
  customTag2?: string;
  customTag3?: string;
};

type OrderDetailsModalProps = {
  initialDetails?: OrderDetailsDraft;
  /** Changes when callers want to discard the local draft for a new source row. */
  resetKey?: React.Key;
  customTag1Options?: string[];
  isCustomTag1Locked?: boolean;
  onClose: () => void;
  onSave: (details: OrderDetailsDraft) => void;
};

const customText1ManualOptionValue = 'manual';
const customText1ManualOptionLabel = 'custom';
const customText1ManualOptionCollisionLabel = 'custom value';
const customText1MenuId = 'custom-text-1-menu';
const blankOrderDetails: OrderDetailsDraft = {
  memo: '',
  customTag1: '',
  customTag2: '',
  customTag3: '',
};
const editableAccountTextFields = ['memo', 'customTag1', 'customTag2'] as const;
type EditableAccountTextField = typeof editableAccountTextFields[number];
type CustomText1MenuOption =
  | {type: 'manual'; key: string; label: string}
  | {type: 'preset'; key: string; label: string; value: string};

/**
 * Returns a non-conflicting label for the manual Custom Text 1 menu option.
 */
function resolveCustomText1ManualOptionLabel(options: string[]) {
  if (!options.includes(customText1ManualOptionLabel)) {
    return customText1ManualOptionLabel;
  }

  let label = customText1ManualOptionCollisionLabel;
  let suffix = 2;
  while (options.includes(label)) {
    label = `${customText1ManualOptionCollisionLabel} ${suffix}`;
    suffix += 1;
  }

  return label;
}

/**
 * Builds menu options with internal identities so preset text cannot trigger manual entry.
 */
function buildCustomText1MenuOptions(options: string[], manualLabel: string): CustomText1MenuOption[] {
  return [
    ...options.map((option, index) => ({
      type: 'preset' as const,
      key: `preset:${index}`,
      label: option,
      value: option,
    })),
    {
      type: 'manual' as const,
      key: customText1ManualOptionValue,
      label: manualLabel,
    },
  ];
}

/**
 * Returns true when a custom text value matches one of the account presets.
 */
function isCustomText1Preset(value: string | undefined, options: string[]) {
  return typeof value === 'string' && options.includes(value);
}

/**
 * Returns a safe preset selection by preferring the current value, then first available preset.
 */
function resolvePresetCustomText1(value: string | undefined, options: string[]) {
  if (isCustomText1Preset(value, options)) {
    return value;
  }

  return options[0] ?? '';
}

/**
 * Returns false after notifying the user when any editable account text value is invalid.
 */
function validateEditableAccountText(details: OrderDetailsDraft) {
  for (const key of editableAccountTextFields) {
    const value = details[key];
    if (typeof value === 'string' && !isValidAccountText(value)) {
      notifyInvalidAccountText(value);
      return false;
    }
  }

  return true;
}

/**
 * Shared order details editor modal used by order-entry and orderBook.
 */
const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  initialDetails,
  resetKey,
  customTag1Options = [],
  isCustomTag1Locked = false,
  onClose,
  onSave,
}) => {
  const [manualCustomText1, setManualCustomText1] = useState(false);
  const [isCustomText1MenuOpen, setIsCustomText1MenuOpen] = useState(false);
  const [details, setDetails] = useState<OrderDetailsDraft>(initialDetails ?? blankOrderDetails);
  const customText1MenuRef = useRef<HTMLDivElement>(null);
  const hasInitialDetails = initialDetails !== undefined;
  const initialMemo = initialDetails?.memo;
  const initialCustomTag1 = initialDetails?.customTag1;
  const initialCustomTag2 = initialDetails?.customTag2;
  const initialCustomTag3 = initialDetails?.customTag3;
  const customTag1OptionsSignature = JSON.stringify(customTag1Options);
  const normalizedCustomTag1Options = useMemo(
    () => customTag1Options.filter((option, index, allOptions) =>
      allOptions.indexOf(option) === index),
    [customTag1OptionsSignature],
  );

  useEffect(() => {
    // Reset only when the source values, preset options, or caller-provided identity change.
    const hasNoPresetOptions = normalizedCustomTag1Options.length === 0;
    const initialIsCustom = hasNoPresetOptions
      || (typeof initialCustomTag1 === 'string'
        && initialCustomTag1 !== ''
        && !isCustomText1Preset(initialCustomTag1, normalizedCustomTag1Options));
    setManualCustomText1(initialIsCustom);
    setIsCustomText1MenuOpen(false);
    const nextDetails = hasInitialDetails
      ? {
        memo: initialMemo,
        customTag1: initialCustomTag1,
        customTag2: initialCustomTag2,
        customTag3: initialCustomTag3,
      }
      : blankOrderDetails;
    setDetails({
      ...nextDetails,
      customTag1: initialIsCustom
        ? (nextDetails.customTag1 ?? '')
        : resolvePresetCustomText1(nextDetails.customTag1, normalizedCustomTag1Options),
    });
  }, [
    resetKey,
    hasInitialDetails,
    initialMemo,
    initialCustomTag1,
    initialCustomTag2,
    initialCustomTag3,
    normalizedCustomTag1Options,
  ]);

  useEffect(() => {
    if (!isCustomText1MenuOpen) {
      return;
    }

    // Closes the custom menu when the user clicks another modal control.
    const handleClickOutside = (event: MouseEvent) => {
      if (!customText1MenuRef.current?.contains(event.target as Node)) {
        setIsCustomText1MenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCustomText1MenuOpen]);

  /**
   * Sends the current local draft to the owning order entry flow.
   */
  const handleSave = () => {
    if (!validateEditableAccountText(details)) {
      return;
    }

    onSave(details);
  };

  /**
   * Updates editable account text fields only when the new value follows QEP account text rules.
   */
  const changeEditableText = (key: EditableAccountTextField, value: string) => {
    if (!isValidAccountText(value)) {
      notifyInvalidAccountText(value);
      setDetails(currentDetails => ({...currentDetails}));
      return;
    }

    setDetails(currentDetails => ({...currentDetails, [key]: value}));
  };

  const selectedPresetCustomText1 = resolvePresetCustomText1(details.customTag1, normalizedCustomTag1Options);
  const customText1ManualOptionText = resolveCustomText1ManualOptionLabel(normalizedCustomTag1Options);
  const customText1MenuOptions = buildCustomText1MenuOptions(
    normalizedCustomTag1Options,
    customText1ManualOptionText,
  );

  /**
   * Applies a preset Custom Text 1 value or switches the field into manual entry mode.
   */
  const selectPresetCustomText1 = (option: CustomText1MenuOption) => {
    if (option.type === 'manual') {
      setDetails(currentDetails => ({...currentDetails, customTag1: ''}));
      setManualCustomText1(true);
    } else {
      setDetails(currentDetails => ({...currentDetails, customTag1: option.value}));
      setManualCustomText1(false);
    }

    setIsCustomText1MenuOpen(false);
  };

  /**
   * Renders the compact preset-reset icon inside the Custom Text 1 editor.
   */
  const UndoIcon = () => (
    <svg className='qep-order-details-modal__undo-icon' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2.5' d='M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6' />
    </svg>
  );

  return createPortal(
    <div className='qep-order-details-modal'>
      <div className='qep-order-details-modal__backdrop' onClick={onClose} />

      <div
        className={cx(
          'qep-order-details-modal__panel',
          {'qep-order-details-modal__panel--menu-open': isCustomText1MenuOpen},
        )}
      >
        <div className='qep-order-details-modal__content'>
          <h2 className='qep-order-details-modal__title'>Order Details</h2>
          <div className='qep-order-details-modal__divider' />
          <p className='qep-order-details-modal__subtitle'>Change the order details for this product.</p>

          <div className='qep-order-details-modal__grid'>
            <div className='qep-order-details-modal__field'>
              <input
                type='text'
                placeholder='Memo'
                className='qep-order-details-modal__input'
                value={details.memo ?? ''}
                maxLength={ACCOUNT_TEXT_MAX_LENGTH}
                onChange={event => {
                  changeEditableText('memo', event.target.value);
                }}
              />
            </div>

            <div className='qep-order-details-modal__field'>
              {isCustomTag1Locked && (
                <input
                  type='text'
                  aria-label='Custom Text 1'
                  readOnly
                  disabled
                  className='qep-order-details-modal__input qep-order-details-modal__input--readonly'
                  value={details.customTag1 ?? ''}
                />
              )}

              {!isCustomTag1Locked && manualCustomText1 && (
                <div className='qep-order-details-modal__input-row'>
                  <input
                    type='text'
                    placeholder='Custom Text 1'
                    className='qep-order-details-modal__input qep-order-details-modal__input--with-action'
                    value={details.customTag1 ?? ''}
                    autoFocus
                    maxLength={ACCOUNT_TEXT_MAX_LENGTH}
                    onChange={event => {
                      changeEditableText('customTag1', event.target.value);
                    }}
                  />
                  <button
                    onClick={() => {
                      setDetails({
                        ...details,
                        customTag1: resolvePresetCustomText1(
                          initialDetails?.customTag1,
                          normalizedCustomTag1Options,
                        ),
                      });
                      setManualCustomText1(false);
                    }}
                    disabled={normalizedCustomTag1Options.length === 0}
                    className='qep-order-details-modal__undo-button'
                    type='button'
                  >
                    <UndoIcon />
                  </button>
                </div>
              )}

              {!isCustomTag1Locked && !manualCustomText1 && (
                <div className='qep-order-details-modal__menu-field' ref={customText1MenuRef}>
                  <button
                    type='button'
                    aria-label='Custom Text 1'
                    aria-haspopup='listbox'
                    aria-expanded={isCustomText1MenuOpen}
                    aria-controls={isCustomText1MenuOpen ? customText1MenuId : undefined}
                    className={cx(
                      'qep-order-details-modal__input qep-order-details-modal__menu-trigger',
                      {'qep-order-details-modal__menu-trigger--open': isCustomText1MenuOpen},
                    )}
                    onClick={() => {
                      setIsCustomText1MenuOpen(isOpen => !isOpen);
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Escape') {
                        setIsCustomText1MenuOpen(false);
                      }
                    }}
                  >
                    <span className='qep-order-details-modal__menu-value'>{selectedPresetCustomText1}</span>
                    <svg className='qep-order-details-modal__menu-icon' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
                    </svg>
                  </button>

                  {isCustomText1MenuOpen && (
                    <div
                      id={customText1MenuId}
                      role='listbox'
                      aria-label='Custom Text 1 options'
                      className='qep-order-details-modal__menu'
                    >
                      {customText1MenuOptions.map(option => (
                        <button
                          key={option.key}
                          type='button'
                          role='option'
                          aria-selected={option.type === 'preset' && option.value === selectedPresetCustomText1}
                          className='qep-order-details-modal__menu-option'
                          onClick={() => {
                            selectPresetCustomText1(option);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className='qep-order-details-modal__field'>
              <QEPInput
                aria-label='Custom Text 2'
                placeholder='Custom Text 2'
                className='qep-order-details-modal__input'
                value={details.customTag2 ?? ''}
                maxLength={ACCOUNT_TEXT_MAX_LENGTH}
                onChange={event => {
                  changeEditableText('customTag2', event.target.value);
                }}
              />
            </div>

            <div className='qep-order-details-modal__field'>
              <input
                type='text'
                readOnly
                disabled
                placeholder='Custom Text 3'
                className='qep-order-details-modal__input qep-order-details-modal__input--readonly'
                value={details.customTag3 ?? ''}
              />
            </div>
          </div>
        </div>

        <div className='qep-order-details-modal__divider' />

        <div className='qep-order-details-modal__footer'>
          <button
            onClick={onClose}
            className='qep-order-details-modal__button qep-order-details-modal__button--cancel'
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className='qep-order-details-modal__button qep-order-details-modal__button--apply'
          >
            Apply changes
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OrderDetailsModal;
